// src/pages/stage/StageDetail.js
import React, { useEffect, useRef, useState } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useLocation, useParams } from "react-router-dom";
import { gql, useMutation } from "@apollo/client";
import { toast } from "react-toastify";
import FormWizard from "react-form-wizard-component";
import "react-form-wizard-component/dist/style.css";

import NavBar from "../../components/common/NavBar";
import DivisionHeader from "../../components/division/DivisionHeader";
import AlertMessage from "../../components/common/AlertMessage";
import StagePlayDaysList from "../../components/stages/StagePlayDaysList";
import GroupsList from "../../components/stages/GroupsList";
import RefereeList from "../../components/stages/RefereeList";
import DualListSelector from "../../components/common/DualListSelector";

import GetStageData from "../../components/stages/queries/GetStageData";
import CreateScheduleMutation from "../../components/stages/mutation/CreateSchedule";
import { confirmDelete } from "../../components/common/confirmDelete";
import useThemeColors from "../../components/organization/portal/useThemeColors";
import { GlobalStyles } from "../../constants/styles";
import useTranslation from "../../i18n/useTranslation";
import { getLocalizedGraphQLError } from "../../util/graphqlError";

function hexToRgba(hex, alpha = 1) {
    if (!hex) return `rgba(0,0,0,${alpha})`;
    let h = hex.replace("#", "");
    if (h.length === 3) {
        h = h.split("").map((c) => c + c).join("");
    }
    const int = parseInt(h, 16);
    const r = (int >> 16) & 255;
    const g = (int >> 8) & 255;
    const b = int & 255;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

const ADD_FIELDS_TO_STAGE_MUTATION = gql`
    mutation AddFieldToStage($stageId: Int!, $fieldIds: [Int]!) {
        addFieldToStage(stageId: $stageId, fieldIds: $fieldIds) {
            success
        }
    }
`;

export default function StageDetailPage() {
    const { t } = useTranslation();
    const colors = useThemeColors();

    const primary = colors.primary || GlobalStyles.colors.canary;
    const textOnPrimary = colors.textOnPrimary || GlobalStyles.colors.woodsmoke;
    const secondary = colors.secondary || primary;
    const textOnSecondary =
        colors.textOnSecondary || colors.textOnPrimary || GlobalStyles.colors.woodsmoke;

    const primary18 = hexToRgba(primary, 0.18);
    const primary22 = hexToRgba(primary, 0.22);
    const primary28 = hexToRgba(primary, 0.28);
    const primary45 = hexToRgba(primary, 0.45);
    const focusShadow = hexToRgba(primary, 0.22);

    const [teams, setTeams] = useState([]);
    const [groups, setGroups] = useState([]);
    const [playDates, setPlayDates] = useState([]);
    const [fields, setFields] = useState([]);
    const [stageFields, setStageFields] = useState([]);

    const params = useParams();
    const location = useLocation();

    const {
        activeSeasonData,
        division,
        stageSlug,
        stageId,
        hasResults,
        scheduleType,
        waivers,
    } = location.state;

    const coachLabel =
        scheduleType === "TRAINING"
            ? t("stageDetail.labels.coaches")
            : t("stageDetail.labels.refereesOrUmpires");

    const [getStageData] = GetStageData();
    const [createScheduleMutation] = CreateScheduleMutation();

    const [submitError, setSubmitError] = useState(null);
    const [referees, setReferees] = useState([]);
    const [stageReferees, setStageReferees] = useState([]);
    const [loading, setLoading] = useState(false);
    const loadedStageSlugRef = useRef(null);

    const [addRemoveFieldToStage] = useMutation(ADD_FIELDS_TO_STAGE_MUTATION);

    useEffect(() => {
        if (!stageSlug || loadedStageSlugRef.current === stageSlug) return;
        loadedStageSlugRef.current = stageSlug;

        getStageData({ variables: { stageSlug } })
            .then((res) => {
                const stage = res.data?.stage;
                if (!stage) return;

                const baseTeams = (stage.teams || []).map((team) => ({
                    id: team.teamId,
                    name: team.teamName,
                    status: "todo",
                    selected: false,
                }));

                const teamToGroup = new Map();
                (stage.groups || []).forEach((group) => {
                    (group.teams || []).forEach((team) => {
                        teamToGroup.set(team.teamId, group.id);
                    });
                });

                const teamsWithStatus = baseTeams.map((team) => ({
                    ...team,
                    status: teamToGroup.get(team.id) ?? "todo",
                }));
                setTeams(teamsWithStatus);

                const groupsList = (stage.groups || []).map((group) => ({
                    id: group.id,
                    name: group.name,
                    color: "info",
                }));
                setGroups(groupsList);

                setPlayDates(stage.playDays || []);

                const tmpStageFields = (stage.fields || []).map((field) => ({
                    id: field.id,
                    name: field.name,
                }));
                setStageFields(tmpStageFields);

                const tmpOrgFields =
                    stage.season?.division?.organization?.fields?.map((field) => ({
                        id: field.id,
                        name: `${field.sportFacility?.name || ""} - ${field.name}`,
                    })) || [];
                setFields(tmpOrgFields);

                setReferees(stage.referees || []);
                setStageReferees(stage.stageReferees || []);
            })
            .catch((err) => {
                toast.error(getLocalizedGraphQLError(err, t, "errors.somethingWentWrong"), {
                    autoClose: 3000,
                    toastId: "games_query_error",
                    position: "top-center",
                    closeOnClick: true,
                });
            });
    }, [getStageData, stageSlug, t]);

    const navBarElem = {
        pageTitle: t("stageDetail.nav.schedule"),
        links: [
            {
                pageTitle: t("stageDetail.nav.organizations"),
                navLink: "/management/organizations",
                active: false,
            },
            {
                pageTitle: t("stageDetail.nav.divisions"),
                navLink: `/management/organizations/${params.orgSlug}/divisions`,
                active: false,
                state: { waivers },
            },
            {
                pageTitle: t("stageDetail.nav.divisionShort"),
                navLink: `/management/organizations/${params.orgSlug}/divisions/${params.divSlug}`,
                active: false,
                state: { waivers },
            },
            { pageTitle: activeSeasonData.name, navLink: null, active: true },
        ],
    };

    const errorHandler = (error) => {
        toast.error(getLocalizedGraphQLError(error, t, "stageDetail.alerts.somethingWentWrong"), {
            autoClose: 3000,
            toastId: "create_schedule_error",
            position: "top-center",
            closeOnClick: true,
        });
    };

    const handleComplete = () => {
        confirmDelete({
            title: t("stageDetail.confirm.title"),
            message: t("stageDetail.confirm.message"),
            confirmLabel: t("stageDetail.buttons.createSchedule"),
            cancelLabel: t("stageDetail.buttons.cancel"),
            danger: false,
            onConfirm: async () => {
                setLoading(true);
                try {
                    const errorMessages = [];

                    if (groups.length === 0) {
                        errorMessages.push({
                            title: t("stageDetail.alerts.tabGroupsErrorTitle"),
                            message: t("stageDetail.alerts.tabGroupsErrorMessage"),
                        });
                    }

                    if (playDates.length === 0) {
                        errorMessages.push({
                            title: t("stageDetail.alerts.tabDateErrorTitle"),
                            message: t("stageDetail.alerts.tabDateErrorMessage"),
                        });
                    }

                    if (stageFields.length === 0) {
                        errorMessages.push({
                            title: t("stageDetail.alerts.tabVenuesErrorTitle"),
                            message: t("stageDetail.alerts.tabVenuesErrorMessage"),
                        });
                    }

                    if (hasResults) {
                        errorMessages.push({
                            title: t("stageDetail.alerts.scheduleErrorTitle"),
                            message: t("stageDetail.alerts.scheduleErrorMessage"),
                        });
                    }

                    if (errorMessages.length > 0) {
                        setSubmitError(errorMessages);
                        return;
                    }

                    const result = await createScheduleMutation({
                        variables: { stageId: parseInt(stageId, 10) },
                    });

                    if (result.data?.createSchedule?.success) {
                        toast.success(t("stageDetail.alerts.scheduleCreated"), {
                            autoClose: 3000,
                            toastId: "create_schedule_success",
                            position: "top-center",
                            closeOnClick: true,
                        });

                        setTimeout(() => {
                            let redirectURL = window.location.href;
                            redirectURL = redirectURL.split("/").slice(0, -2).join("/");
                            window.location.href = redirectURL;
                        }, 3000);
                    } else {
                        errorHandler(
                            result.data?.createSchedule?.error ||
                            new Error(t("stageDetail.alerts.failedToCreateSchedule"))
                        );
                    }
                } catch (e) {
                    errorHandler(e);
                } finally {
                    setLoading(false);
                }
            },
        });
    };

    const handlerFieldSelection = (fieldValues) => {
        const fieldIds = fieldValues.map((item) => parseInt(item.id, 10));

        addRemoveFieldToStage({
            variables: {
                stageId: parseInt(stageId, 10),
                fieldIds,
            },
        }).then(() => {
            setStageFields(fieldValues);
        });
    };

    return (
        <DndProvider backend={HTML5Backend}>
            <div className="content-wrapper">
                <NavBar navBarElem={navBarElem} />

                <section className="content">
                    <div className="container-fluid">
                        <DivisionHeader
                            division={division}
                            activeSeasonData={activeSeasonData}
                        />

                        {submitError &&
                            submitError.map((err, idx) => (
                                <AlertMessage
                                    key={`${err.title}-${idx}`}
                                    title={err.title}
                                    message={err.message}
                                    typeAlert="danger"
                                />
                            ))}

                        <FormWizard
                            stepSize="md"
                            backButtonText={t("common.previous")}
                            nextButtonText={t("common.next")}
                            finishButtonText={t("stageDetail.buttons.createSchedule")}
                            finishButtonTemplate={() => (
                                <div className="wizard-footer-right">
                                    <button
                                        type="button"
                                        className="btn btn-kbola"
                                        onClick={handleComplete}
                                        disabled={loading}
                                    >
                                        {loading ? (
                                            <>
                                                <span
                                                    className="spinner-border spinner-border-sm"
                                                    role="status"
                                                    aria-hidden="true"
                                                />
                                                {" "}
                                                {t("stageDetail.buttons.processing")}
                                            </>
                                        ) : (
                                            t("stageDetail.buttons.createSchedule")
                                        )}
                                    </button>
                                </div>
                            )}
                        >
                            <FormWizard.TabContent
                                title={t("stageDetail.labels.groups")}
                                icon="ti-layers"
                            >
                                <GroupsList
                                    teams={teams}
                                    setTeams={setTeams}
                                    groups={groups}
                                    setGroups={setGroups}
                                    activeSeasonData={activeSeasonData}
                                    stageId={stageId}
                                    scheduleType={scheduleType}
                                />
                            </FormWizard.TabContent>

                            <FormWizard.TabContent
                                title={t("stageDetail.labels.date")}
                                icon="ti-calendar"
                            >
                                <StagePlayDaysList
                                    stageId={stageId}
                                    playDates={playDates}
                                    setPlayDates={setPlayDates}
                                />
                            </FormWizard.TabContent>

                            <FormWizard.TabContent
                                title={t("stageDetail.labels.venues")}
                                icon="ti-pin2"
                            >
                                <DualListSelector
                                    title={t("stageDetail.dualList.venuesTitle")}
                                    description={t("stageDetail.dualList.venuesDescription")}
                                    leftTitle={t("stageDetail.labels.availableFields")}
                                    rightTitle={t("stageDetail.labels.selectedFields")}
                                    items={fields}
                                    selected={stageFields}
                                    getItemLabel={(field) =>
                                        field?.venue?.name
                                            ? `${field.venue.name} ${field.name}`
                                            : field.name
                                    }
                                    onChange={(updated) => handlerFieldSelection(updated)}
                                />
                            </FormWizard.TabContent>

                            <FormWizard.TabContent title={coachLabel} icon="ti-pin2">
                                <RefereeList
                                    referees={referees}
                                    stageReferees={stageReferees}
                                    onSelectionChange={(selected) => console.log(selected)}
                                    stageId={stageId}
                                    scheduleType={scheduleType}
                                />
                            </FormWizard.TabContent>
                        </FormWizard>

                        <style>{`
              @import url("https://cdn.jsdelivr.net/gh/lykmapipo/themify-icons@0.1.2/css/themify-icons.css");

              :root {
                --wizard-primary: ${primary};
                --wizard-text-on-primary: ${textOnPrimary};
                --wizard-secondary: ${secondary};
                --wizard-text-on-secondary: ${textOnSecondary};
                --wizard-text-dark: ${colors.woodsmoke};
              }

              .stepTitle {
                color: rgba(20, 21, 26, 0.55) !important;
                font-weight: 900 !important;
              }

              .stepTitle.active {
                color: var(--wizard-text-on-primary) !important;
              }

              .stepTitle.completed {
                color: rgba(20, 21, 26, 0.75) !important;
              }

              .wizard-progress-with-circle .wizard-progress-bar,
              .wizard-progress-with-circle:before,
              .wizard-progress-with-circle:after {
                display: none !important;
              }

              .wizard-icon-circle {
                border-color: rgba(0, 0, 0, 0.14) !important;
                background: ${primary18} !important;
              }

              .wizard-icon-container {
                background: ${primary22} !important;
                border: 1px solid rgba(0, 0, 0, 0.10) !important;
              }

              .wizard-icon-container i {
                color: var(--wizard-text-on-primary) !important;
              }

              li.active .wizard-icon-circle,
              .wizard-tab.active .wizard-icon-circle {
                border-color: rgba(0, 0, 0, 0.18) !important;
                background: ${primary28} !important;
              }

              li.active .wizard-icon-container,
              .wizard-tab.active .wizard-icon-container {
                background: var(--wizard-primary) !important;
                border: 1px solid rgba(0, 0, 0, 0.14) !important;
              }

              li.completed .wizard-icon-circle,
              .wizard-tab.completed .wizard-icon-circle {
                border-color: rgba(0, 0, 0, 0.14) !important;
                background: ${primary22} !important;
              }

              li.completed .wizard-icon-container,
              .wizard-tab.completed .wizard-icon-container {
                background: ${primary45} !important;
                border: 1px solid rgba(0, 0, 0, 0.12) !important;
              }

              .wizard-btn {
                background: var(--wizard-primary) !important;
                color: var(--wizard-text-on-primary) !important;
                border: 1px solid rgba(0, 0, 0, 0.12) !important;
              }

              .wizard-btn:hover {
                background: var(--wizard-secondary) !important;
                color: var(--wizard-text-on-secondary) !important;
                border-color: rgba(0, 0, 0, 0.14) !important;
              }

              .wizard-btn:focus {
                outline: none !important;
                box-shadow: 0 0 0 0.2rem ${focusShadow} !important;
              }

              .wizard-btn:disabled {
                opacity: 0.65 !important;
                cursor: not-allowed !important;
              }
            `}</style>
                    </div>
                </section>
            </div>
        </DndProvider>
    );
}
